import { React, useState, useEffect, useRef } from "react";
import "./OperationForm.css";
import gsap from "gsap";
import FormHeader from "../../../../Utils/Commons/Forms/FormHeader/FormHeader";
import { useAuth } from "../../../../Security/AuthProvider";
import DropDownOptions from "../../../../Utils/Commons/Forms/DropDownOptions/DropDownOptions";
import RoleAttribution from "../RoleAttribution/RoleAttribution";
import { useNotification } from '../../../../Utils/Commons/Notification/NotificationContext';

const OperationForm = ({ fetchOperations, setShowPopup, operationToEdit }) => {
  const { getCredentials } = useAuth();
  const formRef = useRef(null);
  const [operationData, setOperationData] = useState({
    id: "",
    duration: 4, // Défaut à 4 blocs (1 heure)
    name: "",
    form_id: null,
  });

  const [showRolePopup, setShowRolePopup] = useState(false);
  const [availableForms, setAvailableForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const { notifyError, notifySuccess } = useNotification();

  const options = [
    {
      text: "Gérer les rôles",
      onClick: () => setShowRolePopup(true),
    },
  ];

  // Récupérer la liste des formulaires disponibles
  const fetchForms = async () => {
    setLoadingForms(true);
    try {
      const response = await fetch("/forms/get-all", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });

      if (response.ok) {
        const forms = await response.json();
        setAvailableForms(Array.isArray(forms) ? forms : []);
      } else {
        console.error("Erreur lors de la récupération des formulaires");
        setAvailableForms([]);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des formulaires:", error);
      setAvailableForms([]);
    } finally {
      setLoadingForms(false);
    }
  };

  // Escape key listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowPopup(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setShowPopup]);

  useEffect(() => {
    fetchForms();
  }, []);

  useEffect(() => {
    if (operationToEdit) {
      setOperationData({
        ...operationToEdit,
        form_id: operationToEdit.form_id || null,
      });
    }
  }, [operationToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'form_id') {
      setOperationData({ 
        ...operationData, 
        [name]: value === '' ? null : parseInt(value) 
      });
    } else if (name === 'duration') {
      setOperationData({ 
        ...operationData, 
        [name]: parseInt(value) 
      });
    } else {
      setOperationData({ ...operationData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = operationToEdit
        ? "/operations/update"
        : "/operations/new-operation";
      const method = operationToEdit ? "PUT" : "POST";

      if (!operationData.name || !operationData.duration) {
        console.error("Missing required fields");
        return;
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...operationData,
          id: operationToEdit ? operationToEdit.id : undefined,
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        notifyError(errorData.message || "Network response was not ok");
        throw new Error(errorData.message || "Network response was not ok");
      }

      fetchOperations();
      setShowPopup(false);
      notifySuccess('Opération créée/modifiée avec succès');
    } catch (error) {
      notifyError('Erreur lors de la création/modification de l\'opération');
    }
  };

  // Convertir les blocs en durée lisible
  const formatDuration = (blocks) => {
    const minutes = blocks * 15;
    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMins = minutes % 60;
      if (remainingMins === 0) {
        return `${hours}h`;
      } else {
        return `${hours}h ${remainingMins}min`;
      }
    }
  };

  useEffect(() => {
    gsap.from(formRef.current, {
      opacity: 0.3,
      duration: 0.3,
      x: 10,
    });
  }, []);

  return (
    <>
      <div
        ref={formRef}
        className="operation-form-container general-data-form-container"
        onClick={(e) => e.stopPropagation()}
      >
        {operationToEdit && <DropDownOptions options={options} />}
        <div className="form-content">
          <FormHeader
            title={
              operationToEdit ? "Modifier une opération" : "Créer une opération"
            }
          />
          <form className="create-agent-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Nom de l'opération</label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={operationData.name}
                onChange={handleChange}
                placeholder="Ex: Inspection thermique, Diagnostic électrique..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="duration">
                Durée: {formatDuration(operationData.duration)}
              </label>
              <div className="duration-slider-container">
                <input
                  type="range"
                  id="duration"
                  name="duration"
                  min="1"
                  max="32"
                  step="1"
                  value={operationData.duration}
                  onChange={handleChange}
                  className="duration-slider"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="form_id">Formulaire associé (optionnel)</label>
              {loadingForms ? (
                <div className="loading-forms">Chargement des formulaires...</div>
              ) : (
                <select
                  id="form_id"
                  name="form_id"
                  value={operationData.form_id || ''}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="">Aucun formulaire</option>
                  {availableForms.map((form) => (
                    <option key={form.id} value={form.id}>
                      {form.name}
                      {form.description && ` - ${form.description}`}
                    </option>
                  ))}
                </select>
              )}
              <small className="form-help-text">
                Sélectionnez un formulaire que les agents devront remplir pour cette opération
              </small>
            </div>

            <button type="submit">
              {operationToEdit ? "Mettre à jour" : "Créer"}
            </button>
          </form>
        </div>
      </div>
      <RoleAttribution
        operation={operationToEdit}
        isOpen={showRolePopup}
        onClose={() => setShowRolePopup(false)}
      />
    </>
  );
};

export default OperationForm;
